<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import FlowIssuesDialog from './FlowIssuesDialog.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  builderIssueField,
  focusFlowField,
  metaIssueItems,
} from './flowIssues';
import {
  FIELD_TYPES,
  FLOW_CATEGORIES,
  LIMITS,
  MAX_FIELDS_PER_SCREEN,
  MAX_SCREENS,
  buildFlowJson,
  builderFromFlowJson,
  defaultBuilder,
  hasOptions,
  labelLimit,
  newField,
  newScreen,
  parseFlowJsonText,
  uniqueKey,
  validateBuilder,
} from './flowBuilder';

const props = defineProps({
  inboxOptions: { type: Array, default: () => [] },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);
const { t } = useI18n();

const dialogRef = ref(null);
const flow = ref(null);
const mode = ref('builder');
const jsonText = ref('');
const previewIndex = ref(0);
const showErrors = ref(false);
const issuesRef = ref(null);
const focusOnIssuesClose = ref(false);
const form = reactive({
  inboxId: '',
  name: '',
  categories: ['LEAD_GENERATION'],
  builder: defaultBuilder(),
});

const FIELD_ICONS = {
  text: 'i-hugeicons-text',
  email: 'i-hugeicons-mail-01',
  phone: 'i-hugeicons-call',
  number: 'i-hugeicons-text-font',
  textarea: 'i-hugeicons-note-edit',
  date: 'i-hugeicons-calendar-03',
  dropdown: 'i-hugeicons-arrow-down-01',
  radio: 'i-hugeicons-radio-button',
  checkbox: 'i-hugeicons-check-list',
  optin: 'i-hugeicons-checkmark-circle-02',
};

const isReadOnly = computed(
  () => !!flow.value?.id && flow.value.status !== 'DRAFT'
);
const isImported = computed(
  () =>
    !!flow.value?.id &&
    !flow.value.builder?.screens?.length &&
    !Object.keys(flow.value.flow_json || {}).length
);

const dialogTitle = computed(() => {
  if (!flow.value?.id) return t('WHATSAPP_FLOWS.EDITOR.NEW_TITLE');
  return isReadOnly.value
    ? t('WHATSAPP_FLOWS.EDITOR.VIEW_TITLE')
    : t('WHATSAPP_FLOWS.EDITOR.EDIT_TITLE');
});

const fieldTypeOptions = computed(() =>
  FIELD_TYPES.map(type => ({
    value: type,
    label: t(`WHATSAPP_FLOWS.EDITOR.FIELD_TYPES.${type}`),
  }))
);

const inboxSelectOptions = computed(() => [
  { value: '', label: t('WHATSAPP_FLOWS.SELECT_INBOX') },
  ...props.inboxOptions,
]);

const builderErrors = computed(() => validateBuilder(form.builder));
const jsonResult = computed(() => parseFlowJsonText(jsonText.value));

// Cada pendência local aponta o campo destacado (data-flow-field) e uma dica curta.
const formIssues = computed(() => {
  const issues = [];
  const required = t('WHATSAPP_FLOWS.HINTS.REQUIRED');
  if (!form.inboxId) {
    issues.push({
      key: 'inbox',
      message: t('WHATSAPP_FLOWS.ERRORS.INBOX'),
      hint: required,
    });
  }
  if (!form.name.trim()) {
    issues.push({
      key: 'name',
      message: t('WHATSAPP_FLOWS.ERRORS.NAME'),
      hint: required,
    });
  }
  if (!form.categories.length) {
    issues.push({
      key: 'categories',
      message: t('WHATSAPP_FLOWS.ERRORS.CATEGORIES'),
      hint: t('WHATSAPP_FLOWS.ERRORS.CATEGORIES'),
    });
  }
  if (mode.value === 'builder') {
    builderErrors.value.forEach(error =>
      issues.push({
        key: builderIssueField(error),
        message: t(`WHATSAPP_FLOWS.ERRORS.${error.code}`, error),
        hint: t(`WHATSAPP_FLOWS.HINTS.${error.code}`, error),
      })
    );
  } else {
    jsonResult.value.errors.forEach(code =>
      issues.push({
        key: 'json',
        message: t(`WHATSAPP_FLOWS.ERRORS.${code}`),
        hint: t(`WHATSAPP_FLOWS.ERRORS.${code}`),
      })
    );
  }
  return issues;
});

const formErrors = computed(() => formIssues.value.map(issue => issue.message));

const fieldHints = computed(() => {
  if (!showErrors.value) return {};
  return formIssues.value.reduce((hints, issue) => {
    if (issue.key && !hints[issue.key]) hints[issue.key] = issue.hint;
    return hints;
  }, {});
});

const hintFor = key => fieldHints.value[key] || '';

const metaIssues = computed(() =>
  metaIssueItems(flow.value?.validation_errors || [], t)
);

const showIssues = (options, focusFirstField = false) => {
  focusOnIssuesClose.value = focusFirstField;
  issuesRef.value?.open(options);
};

// Clique na prévia leva ao trecho do formulário correspondente, com realce breve, para o
// usuário não se perder entre telas e campos.
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-n-brand/60'];
const highlight = element => {
  if (!element) return;
  element.classList.add(...HIGHLIGHT_CLASSES);
  setTimeout(() => element.classList.remove(...HIGHLIGHT_CLASSES), 1200);
};
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const goToFlowScreen = async index => {
  previewIndex.value = index;
  await nextTick();
  const card = document.querySelector(`[data-flow-screen="${index}"]`);
  card?.scrollIntoView?.({
    block: 'start',
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
  highlight(card);
};

const goToFlowTarget = async key => {
  await nextTick();
  if (focusFlowField(key)) {
    highlight(document.querySelector(`[data-flow-field="${key}"]`));
  }
};

const handleIssuesClose = async () => {
  if (!focusOnIssuesClose.value) return;
  focusOnIssuesClose.value = false;
  await nextTick();
  const firstKey = formIssues.value.find(issue => issue.key)?.key;
  focusFlowField(firstKey);
};

const showMetaIssues = () => {
  if (!metaIssues.value.length) return;
  showIssues({
    title: t('WHATSAPP_FLOWS.META_ERRORS.TITLE'),
    description: t('WHATSAPP_FLOWS.META_ERRORS.HELP'),
    issues: metaIssues.value,
  });
};

const previewScreen = computed(
  () =>
    form.builder.screens[
      Math.min(previewIndex.value, form.builder.screens.length - 1)
    ]
);

const clone = value => JSON.parse(JSON.stringify(value));

const withKeys = builder => ({
  screens: (builder.screens || []).map(screen => ({
    ...screen,
    key: uniqueKey(),
    fields: (screen.fields || []).map(field => ({
      ...field,
      key: uniqueKey(),
      options: field.options || [],
    })),
  })),
});

const open = ({ flow: current = null, inboxId = '', copy = false } = {}) => {
  flow.value = copy ? null : current;
  showErrors.value = false;
  focusOnIssuesClose.value = false;
  previewIndex.value = 0;
  form.inboxId = String(current?.inbox_id || inboxId || '');
  const baseName = copy
    ? t('WHATSAPP_FLOWS.EDITOR.COPY_NAME', { name: current?.name })
    : current?.name;
  form.name = (baseName || '').slice(0, 200);
  form.categories = current?.categories?.length
    ? [...current.categories]
    : ['LEAD_GENERATION'];
  const hasBuilder = current?.builder?.screens?.length;
  const hasJson = Object.keys(current?.flow_json || {}).length;
  form.builder = hasBuilder
    ? withKeys(clone(current.builder))
    : defaultBuilder();
  mode.value = !hasBuilder && hasJson ? 'json' : 'builder';
  jsonText.value = hasJson ? JSON.stringify(current.flow_json, null, 2) : '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const toggleCategory = category => {
  if (isReadOnly.value) return;
  const index = form.categories.indexOf(category);
  if (index >= 0) form.categories.splice(index, 1);
  else form.categories.push(category);
};

const useJsonMode = () => {
  jsonText.value = JSON.stringify(buildFlowJson(form.builder), null, 2);
  mode.value = 'json';
};

const useBuilderMode = () => {
  mode.value = 'builder';
};

const copyJson = async () => {
  try {
    await copyTextToClipboard(jsonText.value);
    useAlert(t('WHATSAPP_FLOWS.EDITOR.JSON_COPIED'));
  } catch {
    useAlert(t('WHATSAPP_FLOWS.EDITOR.JSON_COPY_FAILED'));
  }
};

// Leva o JSON colado (ex.: de outro Flow) para o construtor visual, preenchendo telas e campos.
const importJsonToBuilder = () => {
  let json;
  try {
    json = JSON.parse(jsonText.value);
  } catch {
    useAlert(t('WHATSAPP_FLOWS.ERRORS.INVALID_JSON'));
    return;
  }
  const result = builderFromFlowJson(json);
  if (!result) {
    useAlert(t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT_EMPTY'));
    return;
  }
  form.builder = result.builder;
  previewIndex.value = 0;
  mode.value = 'builder';
  if (result.skipped.length) {
    showIssues({
      title: t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT_PARTIAL_TITLE'),
      description: t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT_PARTIAL'),
      issues: result.skipped.map(name =>
        name === 'screens'
          ? t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT_TOO_MANY_SCREENS', {
              max: MAX_SCREENS,
            })
          : name
      ),
    });
  } else {
    useAlert(t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORTED'));
  }
};

const addScreen = () => {
  if (form.builder.screens.length >= MAX_SCREENS) return;
  form.builder.screens.push(newScreen());
  previewIndex.value = form.builder.screens.length - 1;
};

const removeScreen = index => {
  if (form.builder.screens.length === 1) return;
  form.builder.screens.splice(index, 1);
  previewIndex.value = Math.max(
    0,
    Math.min(previewIndex.value, form.builder.screens.length - 1)
  );
};

const moveScreen = (index, direction) => {
  const target = index + direction;
  const screens = form.builder.screens;
  if (target < 0 || target >= screens.length) return;
  [screens[index], screens[target]] = [screens[target], screens[index]];
};

const addField = screen => {
  if (screen.fields.length >= MAX_FIELDS_PER_SCREEN) return;
  screen.fields.push(newField('text'));
};

const removeField = (screen, index) => screen.fields.splice(index, 1);

const moveField = (screen, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= screen.fields.length) return;
  [screen.fields[index], screen.fields[target]] = [
    screen.fields[target],
    screen.fields[index],
  ];
};

const changeFieldType = (field, type) => {
  field.type = type;
  if (hasOptions(type) && !field.options.length) {
    field.options = [{ title: '' }, { title: '' }];
  }
  if (type === 'optin') field.required = false;
  if (type !== 'optin') field.talkviaConsent = false;
};

// Só um aceite por Flow vira opt-in de comunicações da Talkvia (campo talkvia_opt_in).
const toggleTalkviaConsent = (field, value) => {
  if (value) {
    form.builder.screens.forEach(screen =>
      screen.fields.forEach(item => {
        item.talkviaConsent = false;
      })
    );
  }
  field.talkviaConsent = value;
};

const addOption = field => field.options.push({ title: '' });
const removeOption = (field, index) => {
  if (field.options.length > 1) field.options.splice(index, 1);
};

const handleConfirm = () => {
  if (isReadOnly.value || props.isSaving) return;
  showErrors.value = true;
  if (formErrors.value.length) {
    showIssues(
      {
        title: t('WHATSAPP_FLOWS.ISSUES.TITLE'),
        description: t('WHATSAPP_FLOWS.ISSUES.DESCRIPTION'),
        issues: formErrors.value,
        confirmLabel: t('WHATSAPP_FLOWS.ISSUES.REVIEW'),
      },
      true
    );
    return;
  }

  const isBuilder = mode.value === 'builder';
  emit('save', {
    id: flow.value?.id || null,
    inbox_id: form.inboxId,
    name: form.name.trim(),
    categories: [...form.categories],
    flow_json: isBuilder ? buildFlowJson(form.builder) : jsonResult.value.json,
    builder: isBuilder ? { screens: clone(form.builder.screens) } : {},
  });
};

const setFlow = updated => {
  flow.value = updated;
};

// Falhas da API (já em linguagem humana) aparecem no modal do sistema, nunca como bloco no formulário.
const setError = message => {
  showIssues({
    title: t('WHATSAPP_FLOWS.ISSUES.SAVE_ERROR_TITLE'),
    issues: [message || t('WHATSAPP_FLOWS.EDITOR.SAVE_ERROR')],
  });
};

defineExpose({ open, close, setFlow, setError, showMetaIssues });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="6xl"
    position="top"
    overflow-y-auto
    sticky-header-footer
    solid
    :close-on-click-outside="false"
    :title="dialogTitle"
    :description="t('WHATSAPP_FLOWS.EDITOR.DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_FLOWS.EDITOR.SAVE')"
    :show-confirm-button="!isReadOnly"
    :cancel-button-label="t('WHATSAPP_FLOWS.CLOSE')"
    :is-loading="isSaving"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-5">
      <p
        v-if="isReadOnly"
        class="mb-0 flex items-start gap-2 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
      >
        <span class="i-hugeicons-information-circle mt-0.5 size-4 shrink-0" />
        {{ t('WHATSAPP_FLOWS.EDITOR.READ_ONLY_NOTICE') }}
      </p>
      <p
        v-else-if="isImported"
        class="mb-0 flex items-start gap-2 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
      >
        <span class="i-hugeicons-information-circle mt-0.5 size-4 shrink-0" />
        {{ t('WHATSAPP_FLOWS.EDITOR.IMPORTED_NOTICE') }}
      </p>

      <div
        v-if="metaIssues.length"
        class="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-12"
      >
        <span class="flex min-w-0 items-center gap-2">
          <span class="i-hugeicons-alert-circle size-4 shrink-0" />
          {{
            t('WHATSAPP_FLOWS.VALIDATION_COUNT', { count: metaIssues.length })
          }}
        </span>
        <NextButton
          type="button"
          size="xs"
          variant="faded"
          color="amber"
          :label="t('WHATSAPP_FLOWS.META_ERRORS.VIEW')"
          @click="showMetaIssues"
        />
      </div>

      <section
        class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 md:grid-cols-2"
      >
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('WHATSAPP_FLOWS.INBOX') }}
          <NextSelect
            v-model="form.inboxId"
            data-flow-field="inbox"
            :options="inboxSelectOptions"
            :disabled="!!flow?.id"
            :error="hintFor('inbox')"
            class="w-full [&>select]:w-full"
          />
          <span v-if="hintFor('inbox')" class="text-xs text-n-ruby-11">
            {{ hintFor('inbox') }}
          </span>
        </label>
        <NextInput
          v-model="form.name"
          data-flow-field="name"
          :label="t('WHATSAPP_FLOWS.EDITOR.NAME')"
          :placeholder="t('WHATSAPP_FLOWS.EDITOR.NAME_PLACEHOLDER')"
          :disabled="isReadOnly"
          :message="hintFor('name')"
          :message-type="hintFor('name') ? 'error' : 'info'"
          maxlength="200"
        />
        <fieldset class="md:col-span-2" data-flow-field="categories">
          <legend class="mb-1.5 text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_FLOWS.CATEGORIES.LABEL') }}
          </legend>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="category in FLOW_CATEGORIES"
              :key="category"
              type="button"
              class="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
              :class="
                form.categories.includes(category)
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              :aria-pressed="form.categories.includes(category)"
              :disabled="isReadOnly"
              @click="toggleCategory(category)"
            >
              {{ t(`WHATSAPP_FLOWS.CATEGORIES.${category}`) }}
            </button>
          </div>
          <p
            class="mb-0 mt-1.5 text-xs"
            :class="
              hintFor('categories') ? 'text-n-ruby-11' : 'text-n-slate-11'
            "
          >
            {{ hintFor('categories') || t('WHATSAPP_FLOWS.CATEGORIES.HELP') }}
          </p>
        </fieldset>
      </section>

      <nav
        class="flex gap-2 border-b border-n-weak pb-3"
        :aria-label="dialogTitle"
      >
        <button
          type="button"
          class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
          :class="
            mode === 'builder'
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-current="mode === 'builder' ? 'page' : undefined"
          @click="useBuilderMode"
        >
          <span class="i-hugeicons-layout-02 size-4" />
          {{ t('WHATSAPP_FLOWS.EDITOR.MODE_BUILDER') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
          :class="
            mode === 'json'
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-current="mode === 'json' ? 'page' : undefined"
          @click="mode === 'json' ? null : useJsonMode()"
        >
          <span class="i-hugeicons-source-code size-4" />
          {{ t('WHATSAPP_FLOWS.EDITOR.MODE_JSON') }}
        </button>
      </nav>

      <section v-if="mode === 'json'" class="flex flex-col gap-3">
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('WHATSAPP_FLOWS.EDITOR.JSON_HELP') }}
        </p>
        <NextTextArea
          v-model="jsonText"
          data-flow-field="json"
          :message="hintFor('json')"
          :message-type="hintFor('json') ? 'error' : 'info'"
          :label="t('WHATSAPP_FLOWS.EDITOR.JSON_LABEL')"
          :max-length="1000000"
          :disabled="isReadOnly"
          min-height="20rem"
          max-height="32rem"
          custom-text-area-class="font-mono text-xs"
          resize
        />
        <div class="flex flex-wrap gap-2">
          <NextButton
            type="button"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-hugeicons-copy-01"
            :label="t('WHATSAPP_FLOWS.EDITOR.JSON_COPY')"
            :disabled="!jsonText.trim()"
            data-test="flow-json-copy"
            @click="copyJson"
          />
          <NextButton
            v-if="!isReadOnly"
            v-tooltip.top="t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT_HELP')"
            type="button"
            size="sm"
            variant="faded"
            color="blue"
            icon="i-hugeicons-arrow-data-transfer-horizontal"
            :label="t('WHATSAPP_FLOWS.EDITOR.JSON_IMPORT')"
            :disabled="!jsonText.trim()"
            data-test="flow-json-import"
            @click="importJsonToBuilder"
          />
          <NextButton
            v-if="!isReadOnly"
            v-tooltip.top="t('WHATSAPP_FLOWS.EDITOR.JSON_FROM_BUILDER_HELP')"
            type="button"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-hugeicons-layout-02"
            :label="t('WHATSAPP_FLOWS.EDITOR.JSON_FROM_BUILDER')"
            @click="useJsonMode"
          />
        </div>
      </section>

      <div
        v-else
        class="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]"
      >
        <div class="flex min-w-0 flex-col gap-4">
          <article
            v-for="(screen, screenIndex) in form.builder.screens"
            :key="screen.key"
            class="flex scroll-mt-4 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-shadow"
            :data-flow-screen="screenIndex"
            @focusin="previewIndex = screenIndex"
          >
            <header class="flex flex-wrap items-center justify-between gap-2">
              <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{
                  t('WHATSAPP_FLOWS.EDITOR.SCREEN', { number: screenIndex + 1 })
                }}
              </h4>
              <div v-if="!isReadOnly" class="flex gap-1">
                <NextButton
                  type="button"
                  size="xs"
                  variant="ghost"
                  color="slate"
                  icon="i-hugeicons-arrow-up-01"
                  :disabled="screenIndex === 0"
                  :aria-label="t('WHATSAPP_FLOWS.EDITOR.MOVE_UP')"
                  @click="moveScreen(screenIndex, -1)"
                />
                <NextButton
                  type="button"
                  size="xs"
                  variant="ghost"
                  color="slate"
                  icon="i-hugeicons-arrow-down-01"
                  :disabled="screenIndex === form.builder.screens.length - 1"
                  :aria-label="t('WHATSAPP_FLOWS.EDITOR.MOVE_DOWN')"
                  @click="moveScreen(screenIndex, 1)"
                />
                <NextButton
                  v-if="form.builder.screens.length > 1"
                  type="button"
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  icon="i-hugeicons-delete-02"
                  :aria-label="t('WHATSAPP_FLOWS.EDITOR.REMOVE_SCREEN')"
                  @click="removeScreen(screenIndex)"
                />
              </div>
            </header>

            <div class="grid gap-3 md:grid-cols-2">
              <NextInput
                v-model="screen.title"
                :data-flow-field="`s${screenIndex}.title`"
                :message="hintFor(`s${screenIndex}.title`)"
                :message-type="
                  hintFor(`s${screenIndex}.title`) ? 'error' : 'info'
                "
                :label="t('WHATSAPP_FLOWS.EDITOR.SCREEN_TITLE')"
                :maxlength="String(LIMITS.screenTitle)"
                :disabled="isReadOnly"
              />
              <NextInput
                v-model="screen.heading"
                :data-flow-field="`s${screenIndex}.heading`"
                :message="hintFor(`s${screenIndex}.heading`)"
                :message-type="
                  hintFor(`s${screenIndex}.heading`) ? 'error' : 'info'
                "
                :label="t('WHATSAPP_FLOWS.EDITOR.SCREEN_HEADING')"
                :maxlength="String(LIMITS.heading)"
                :disabled="isReadOnly"
              />
            </div>
            <NextTextArea
              v-model="screen.body"
              :data-flow-field="`s${screenIndex}.body`"
              :message="hintFor(`s${screenIndex}.body`)"
              :message-type="hintFor(`s${screenIndex}.body`) ? 'error' : 'info'"
              :label="t('WHATSAPP_FLOWS.EDITOR.SCREEN_BODY')"
              :max-length="LIMITS.body"
              :disabled="isReadOnly"
              resize
            />

            <div class="flex flex-col gap-3">
              <p
                class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              >
                {{ t('WHATSAPP_FLOWS.EDITOR.FIELDS') }}
              </p>
              <div
                v-for="(field, fieldIndex) in screen.fields"
                :key="field.key"
                class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
              >
                <div
                  class="grid gap-3 md:grid-cols-[12rem_minmax(0,1fr)_auto] md:items-end"
                >
                  <label
                    class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('WHATSAPP_FLOWS.EDITOR.FIELD_TYPE') }}
                    <NextSelect
                      :model-value="field.type"
                      :options="fieldTypeOptions"
                      :disabled="isReadOnly"
                      class="w-full [&>select]:w-full"
                      @update:model-value="changeFieldType(field, $event)"
                    />
                  </label>
                  <NextInput
                    v-model="field.label"
                    :data-flow-field="`s${screenIndex}.f${fieldIndex}.label`"
                    :message="hintFor(`s${screenIndex}.f${fieldIndex}.label`)"
                    :message-type="
                      hintFor(`s${screenIndex}.f${fieldIndex}.label`)
                        ? 'error'
                        : 'info'
                    "
                    :label="t('WHATSAPP_FLOWS.EDITOR.FIELD_LABEL')"
                    :maxlength="String(labelLimit(field.type))"
                    :disabled="isReadOnly"
                  />
                  <div v-if="!isReadOnly" class="flex gap-1">
                    <NextButton
                      type="button"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      icon="i-hugeicons-arrow-up-01"
                      :disabled="fieldIndex === 0"
                      :aria-label="t('WHATSAPP_FLOWS.EDITOR.MOVE_UP')"
                      @click="moveField(screen, fieldIndex, -1)"
                    />
                    <NextButton
                      type="button"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      icon="i-hugeicons-arrow-down-01"
                      :disabled="fieldIndex === screen.fields.length - 1"
                      :aria-label="t('WHATSAPP_FLOWS.EDITOR.MOVE_DOWN')"
                      @click="moveField(screen, fieldIndex, 1)"
                    />
                    <NextButton
                      type="button"
                      size="xs"
                      variant="ghost"
                      color="ruby"
                      icon="i-hugeicons-delete-02"
                      :aria-label="t('WHATSAPP_FLOWS.EDITOR.REMOVE_FIELD')"
                      @click="removeField(screen, fieldIndex)"
                    />
                  </div>
                </div>
                <div
                  class="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end"
                >
                  <NextInput
                    v-if="field.type !== 'optin' && !hasOptions(field.type)"
                    v-model="field.helper"
                    :data-flow-field="`s${screenIndex}.f${fieldIndex}.helper`"
                    :message="hintFor(`s${screenIndex}.f${fieldIndex}.helper`)"
                    :message-type="
                      hintFor(`s${screenIndex}.f${fieldIndex}.helper`)
                        ? 'error'
                        : 'info'
                    "
                    :label="t('WHATSAPP_FLOWS.EDITOR.FIELD_HELPER')"
                    :maxlength="String(LIMITS.helper)"
                    :disabled="isReadOnly"
                  />
                  <span v-else />
                  <label
                    class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-12 md:pb-2"
                  >
                    <Checkbox v-model="field.required" :disabled="isReadOnly" />
                    {{ t('WHATSAPP_FLOWS.EDITOR.FIELD_REQUIRED') }}
                  </label>
                </div>
                <label
                  v-if="field.type === 'optin'"
                  class="flex cursor-pointer items-start gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm text-n-slate-12"
                  data-test="flow-optin-talkvia"
                >
                  <Checkbox
                    :model-value="!!field.talkviaConsent"
                    :disabled="isReadOnly"
                    @update:model-value="toggleTalkviaConsent(field, $event)"
                  />
                  <span class="min-w-0">
                    <span class="block font-medium">
                      {{ t('WHATSAPP_FLOWS.EDITOR.TALKVIA_CONSENT') }}
                    </span>
                    <span class="mt-0.5 block text-xs text-n-slate-11">
                      {{ t('WHATSAPP_FLOWS.EDITOR.TALKVIA_CONSENT_HELP') }}
                    </span>
                  </span>
                </label>
                <div
                  v-if="hasOptions(field.type)"
                  class="flex flex-col gap-2"
                  :data-flow-field="`s${screenIndex}.f${fieldIndex}.options`"
                >
                  <p class="mb-0 text-xs font-medium text-n-slate-11">
                    {{ t('WHATSAPP_FLOWS.EDITOR.OPTIONS') }}
                  </p>
                  <p
                    v-if="hintFor(`s${screenIndex}.f${fieldIndex}.options`)"
                    class="mb-0 text-xs text-n-ruby-11"
                  >
                    {{ hintFor(`s${screenIndex}.f${fieldIndex}.options`) }}
                  </p>
                  <div
                    v-for="(option, optionIndex) in field.options"
                    :key="optionIndex"
                    class="flex items-end gap-2"
                  >
                    <NextInput
                      v-model="option.title"
                      class="flex-1"
                      :label="
                        t('WHATSAPP_FLOWS.EDITOR.OPTION', {
                          number: optionIndex + 1,
                        })
                      "
                      :maxlength="String(LIMITS.optionTitle)"
                      :disabled="isReadOnly"
                    />
                    <NextButton
                      v-if="!isReadOnly && field.options.length > 1"
                      type="button"
                      size="sm"
                      variant="ghost"
                      color="ruby"
                      icon="i-hugeicons-delete-02"
                      :aria-label="t('WHATSAPP_FLOWS.EDITOR.REMOVE_OPTION')"
                      @click="removeOption(field, optionIndex)"
                    />
                  </div>
                  <NextButton
                    v-if="!isReadOnly"
                    type="button"
                    size="xs"
                    variant="faded"
                    color="slate"
                    class="self-start"
                    icon="i-hugeicons-add-01"
                    :label="t('WHATSAPP_FLOWS.EDITOR.ADD_OPTION')"
                    @click="addOption(field)"
                  />
                </div>
              </div>
              <NextButton
                v-if="
                  !isReadOnly && screen.fields.length < MAX_FIELDS_PER_SCREEN
                "
                type="button"
                size="sm"
                variant="faded"
                class="self-start"
                icon="i-hugeicons-add-01"
                :label="t('WHATSAPP_FLOWS.EDITOR.ADD_FIELD')"
                @click="addField(screen)"
              />
            </div>

            <NextInput
              v-model="screen.footerLabel"
              :data-flow-field="`s${screenIndex}.footer`"
              :label="t('WHATSAPP_FLOWS.EDITOR.FOOTER_LABEL')"
              :maxlength="String(LIMITS.footer)"
              :disabled="isReadOnly"
              :message-type="
                hintFor(`s${screenIndex}.footer`) ? 'error' : 'info'
              "
              :message="
                hintFor(`s${screenIndex}.footer`) ||
                (screenIndex === form.builder.screens.length - 1
                  ? t('WHATSAPP_FLOWS.EDITOR.FOOTER_HINT_LAST')
                  : t('WHATSAPP_FLOWS.EDITOR.FOOTER_HINT_NEXT'))
              "
            />
          </article>

          <NextButton
            v-if="!isReadOnly && form.builder.screens.length < MAX_SCREENS"
            type="button"
            variant="faded"
            class="self-start"
            icon="i-hugeicons-add-square"
            :label="t('WHATSAPP_FLOWS.EDITOR.ADD_SCREEN')"
            @click="addScreen"
          />
        </div>

        <aside class="flex flex-col gap-2 lg:sticky lg:top-0">
          <p
            class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('WHATSAPP_FLOWS.EDITOR.PREVIEW_TITLE') }}
          </p>
          <div
            v-if="form.builder.screens.length > 1"
            class="flex flex-wrap gap-1"
          >
            <button
              v-for="(screen, index) in form.builder.screens"
              :key="screen.key"
              type="button"
              class="rounded-md px-2 py-1 text-xs"
              :class="
                previewIndex === index
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              :aria-label="
                t('WHATSAPP_FLOWS.EDITOR.GO_TO_SCREEN', { number: index + 1 })
              "
              @click="goToFlowScreen(index)"
            >
              {{ index + 1 }}
            </button>
          </div>
          <div
            v-if="previewScreen"
            class="flex min-h-80 flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2"
          >
            <button
              type="button"
              class="flex items-center gap-2 border-0 border-b border-solid border-n-weak px-4 py-3 text-start text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-1"
              :title="t('WHATSAPP_FLOWS.EDITOR.GO_TO_FIELD')"
              @click="goToFlowTarget(`s${previewIndex}.title`)"
            >
              <span class="i-hugeicons-cancel-01 size-4 text-n-slate-11" />
              <span class="truncate">{{ previewScreen.title || '—' }}</span>
            </button>
            <div class="flex flex-1 flex-col gap-3 px-4 py-3">
              <button
                v-if="previewScreen.heading"
                type="button"
                class="-mx-1 rounded px-1 text-start text-base font-semibold text-n-slate-12 hover:bg-n-alpha-1"
                :title="t('WHATSAPP_FLOWS.EDITOR.GO_TO_FIELD')"
                @click="goToFlowTarget(`s${previewIndex}.heading`)"
              >
                {{ previewScreen.heading }}
              </button>
              <button
                v-if="previewScreen.body"
                type="button"
                class="-mx-1 whitespace-pre-wrap rounded px-1 text-start text-sm text-n-slate-11 hover:bg-n-alpha-1"
                :title="t('WHATSAPP_FLOWS.EDITOR.GO_TO_FIELD')"
                @click="goToFlowTarget(`s${previewIndex}.body`)"
              >
                {{ previewScreen.body }}
              </button>
              <button
                v-for="(field, fieldIndex) in previewScreen.fields"
                :key="field.key"
                type="button"
                class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-start text-xs text-n-slate-11 hover:border-n-brand/50 hover:bg-n-alpha-1"
                :title="t('WHATSAPP_FLOWS.EDITOR.GO_TO_FIELD')"
                @click="goToFlowTarget(`s${previewIndex}.f${fieldIndex}.label`)"
              >
                <span
                  :class="FIELD_ICONS[field.type]"
                  class="size-4 shrink-0"
                />
                <span class="truncate">
                  {{
                    field.label ||
                    t(`WHATSAPP_FLOWS.EDITOR.FIELD_TYPES.${field.type}`)
                  }}
                  <template v-if="field.required && field.type !== 'optin'"
                    >*</template
                  >
                </span>
              </button>
            </div>
            <div class="border-t border-n-weak p-3">
              <button
                type="button"
                class="w-full rounded-full bg-n-teal-9 px-4 py-2 text-center text-sm font-medium text-white hover:bg-n-teal-10"
                :title="t('WHATSAPP_FLOWS.EDITOR.GO_TO_FIELD')"
                @click="goToFlowTarget(`s${previewIndex}.footer`)"
              >
                {{ previewScreen.footerLabel || '—' }}
              </button>
            </div>
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('WHATSAPP_FLOWS.EDITOR.PREVIEW_CLICK_HELP') }}
            {{ t('WHATSAPP_FLOWS.EDITOR.PREVIEW_HELP') }}
          </p>
        </aside>
      </div>
    </div>
  </Dialog>
  <FlowIssuesDialog ref="issuesRef" @close="handleIssuesClose" />
</template>
